import React from "react";
import { useTranslations } from "use-intl";
import { useBankCatalog } from '@/hooks/useBankCatalog';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from "@/utils/paymentUtils";
import { ArrowUp, ArrowDown, Loader2, AlertTriangle } from "lucide-react";
import { useTableSort } from "@/hooks/useTableSort";
import { useCompanySyncStatus, isSyncStillImporting, isSyncStopped } from "@/hooks/useCompanySyncStatus";
import { cn } from "@/lib/utils";
import { Button } from '@/components/ui/button';
import { useConsentContextOptional } from '@/components/providers/ConsentProvider';
import { renewalRequestFor } from '@/lib/renewalRequest';

interface BankAccount {
  id: string;
  bankName: string;
  maskedNumber: string | null;
  resourceId?: string;
  balance: number | null;
  currency: string;
  logoUrl: string;
  bic: string;
  // FIN-885: a bic-less bank's identity, which renewing its access is keyed on.
  providerRef?: string | null;
  countryCode?: string | null;
  syncFailed?: boolean;
  status?: 'ok' | 'unavailable' | 'consent_invalid';
}

interface BankAccountsListProps {
  bankAccounts: BankAccount[];
}

const BankAccountsList: React.FC<BankAccountsListProps> = ({ bankAccounts }) => {
  const t = useTranslations('consultantTransactions.dashboard');
  const { getBankDisplayName: displayNameFor, getBankLogo: logoFor } = useBankCatalog();
  const { sortedData, sortConfig, handleSort } = useTableSort<BankAccount, keyof BankAccount>(bankAccounts);
  // FIN-568 (bank-account-importing-indicator): the primary place this ever renders in practice —
  // onboarding's post-connect redirect lands here, not on /bank (BankConnectionDialog.tsx
  // navigates to /overview once isOnboarding is true).
  const { statusByAccountId } = useCompanySyncStatus(bankAccounts.map((a) => a.id));
  // FIN-863: Renew access on an Access needed row opens the global renewal dialog for it,
  // the same as the Bank table's row.
  const consentContext = useConsentContextOptional();


  const getBankDisplayName = (account: BankAccount) => displayNameFor(account.bic, account.bankName);

  const getBankLogo = (account: BankAccount) => logoFor(account.bic, account.logoUrl);

  const renderSortIcon = (key: keyof BankAccount) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4 inline" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4 inline" />
    );
  };

  const renderBalanceCell = (account: BankAccount) => {
    if (account.status === 'consent_invalid' && consentContext) {
      return (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 font-semibold underline underline-offset-2"
          onClick={() => consentContext.setConsentRequiredError(renewalRequestFor([account]))}
        >
          {t('bankAccounts.renewAccess')}
        </Button>
      );
    }
    if (account.syncFailed) {
      return <span className="text-muted-foreground font-normal">{t('bankAccounts.unavailable')}</span>;
    }
    return formatCurrency(account.balance, account.currency);
  };

  return (
    <Card className="flex flex-col light-blue-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {t('bankAccounts.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-grow">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead 
                className={cn("cursor-pointer", sortConfig.key === 'bankName' && "text-primary")}
                onClick={() => handleSort('bankName')}
              >
                {t('bankAccounts.bank')} {renderSortIcon('bankName')}
              </TableHead>
              <TableHead 
                className={cn("cursor-pointer", sortConfig.key === 'balance' && "text-primary")}
                onClick={() => handleSort('balance')}
              >
                {t('bankAccounts.balance')} {renderSortIcon('balance')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map(account => (
              <TableRow key={account.id}>
                <TableCell className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-md border p-1 flex items-center justify-center bg-white">
                    <img 
                      src={getBankLogo(account)} 
                      alt={t('bankAccounts.logoAlt', { bank: getBankDisplayName(account) })}
                      className="h-6 w-6 object-contain" 
                      onError={e => {
                        (e.target as HTMLImageElement).src = '/placeholder.svg';
                      }} 
                    />
                  </div>
                  <span>{getBankDisplayName(account)}</span>
                  {isSyncStillImporting(statusByAccountId.get(account.id)) && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {t('bankAccounts.importing')}
                    </span>
                  )}
                  {/* Also shown when the scheduler has given up (FIN-745): before that, an
                      exhausted retry budget left the row spinning "Importing…" forever, because
                      nothing ever moves it out of 'failed' again. */}
                  {!isSyncStillImporting(statusByAccountId.get(account.id)) && account.status === 'consent_invalid' && (
                    <span className="flex items-center gap-1 text-xs text-destructive">
                      <span className="h-1.5 w-1.5 rounded-full bg-current" />
                      {t('bankAccounts.accessNeeded')}
                    </span>
                  )}
                  {!isSyncStillImporting(statusByAccountId.get(account.id))
                    && account.status !== 'consent_invalid'
                    && (account.syncFailed || isSyncStopped(statusByAccountId.get(account.id))) && (
                    <span className="flex items-center gap-1 text-xs text-amber-600">
                      <AlertTriangle className="h-3 w-3" />
                      {t('bankAccounts.couldntSync')}
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  {renderBalanceCell(account)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      <CardFooter className="text-xs text-muted-foreground mt-auto pt-2 border-t">
        {t('lastUpdated', { minutes: 10 })}
      </CardFooter>
    </Card>
  );
};

export default BankAccountsList;
